<template>
  <div class="template-container">
      <!-- 操作区域 -->
      <div class="action-bar">
          <el-button type="primary" @click="fetchTemplates" :loading="loading">
              <el-icon>
                  <Refresh />
              </el-icon>
              刷新模板
          </el-button>

          <el-button type="success" @click="syncTemplates" :loading="syncing">
              <el-icon>
                  <Download />
              </el-icon>
              同步模板
          </el-button>

          <div style="flex: 1"></div>

          <el-input v-model="searchKeyword" placeholder="搜索模板标题或内容" clearable style="width: 300px; margin-right: 15px"
              @keyup.enter="handleSearch" @clear="handleSearch">
              <template #prefix>
                  <el-icon>
                      <Search />
                  </el-icon>
              </template>
          </el-input>
      </div>

      <!-- 模板列表 -->
      <div class="template-list">
          <el-card v-for="template in filteredTemplates" :key="template.template_id" class="template-card">
              <template #header>
                  <div class="template-header">
                      <h3 class="template-title">{{ template.title }}</h3>
                      <el-tag type="primary" size="small">{{ template.template_id }}</el-tag>
                  </div>
              </template>

              <div class="template-content">
                  <div class="template-industry" v-if="template.primary_industry">
                      <strong>行业:</strong> {{ template.primary_industry }} - {{ template.deputy_industry }}
                  </div>

                  <div class="template-content-text">
                      <strong>内容:</strong>
                      <div class="content-preview">{{ formatTemplateContent(template.content) }}</div>
                  </div>

                  <div class="template-example" v-if="template.example">
                      <strong>示例:</strong>
                      <div class="example-preview">{{ template.example }}</div>
                  </div>

                  <div class="template-keywords">
                      <strong>关键词:</strong>
                      <div class="keywords-list">
                          <el-tag v-for="keyword in extractKeywords(template.content)" :key="keyword" size="small"
                              class="keyword-tag">
                              {{ keyword }}
                          </el-tag>
                      </div>
                  </div>
              </div>

              <template #footer>
                  <div class="template-actions">
                      <el-button type="primary" size="small" @click="selectTemplate(template)">
                          <el-icon>
                              <Select />
                          </el-icon>
                          选择发送
                      </el-button>
                      <el-button type="info" size="small" @click="copyTemplateId(template.template_id)">
                          <el-icon>
                              <CopyDocument />
                          </el-icon>
                          复制ID
                      </el-button>
                  </div>
              </template>
          </el-card>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container" v-if="pagination.total > 0">
          <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
              :total="pagination.total" :page-sizes="[4, 8, 12, 20]" layout="total, sizes, prev, pager, next, jumper"
              @size-change="handlePageChange" @current-change="handlePageChange" />
      </div>

      <!-- 空状态 -->
      <el-empty v-if="filteredTemplates.length === 0 && !loading" description="暂无模板数据">
          <el-button type="primary" @click="syncTemplates">同步模板</el-button>
      </el-empty>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
          <el-skeleton :rows="5" animated />
      </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import {
  Refresh, Download, Search, Select, CopyDocument
} from '@element-plus/icons-vue'

export default {
  name: 'WechatTemplateView',
  components: { Refresh, Download, Search, Select, CopyDocument },
  props: {
      accountId: {
          type: Number,
          required: true
      },
      storeId: {
          type: Number,
          required: true
      }
  },
  data() {
      return {
          loading: false,
          syncing: false,
          searchKeyword: '',
          templates: [],
          pagination: {
              page: 1,
              pageSize: 4,
              total: 0
          }
      }
  },
  computed: {
      filteredTemplates() {
          if (!this.searchKeyword) {
              return this.templates;
          }

          const keyword = this.searchKeyword.toLowerCase();
          return this.templates.filter(template =>
              template.title.toLowerCase().includes(keyword) ||
              template.content.toLowerCase().includes(keyword) ||
              (template.primary_industry && template.primary_industry.toLowerCase().includes(keyword)) ||
              template.template_id.toLowerCase().includes(keyword)
          );
      }
  },
  mounted() {
      this.fetchTemplates();
  },
  methods: {
      // 获取模板列表
      async fetchTemplates() {
          this.loading = true;
          try {
              const { data } = await axios.post(
                  `${config.BASE_URL}/shop/api/wechat_account.php`,
                  {
                      action: 'get_templates',
                      account_id: this.accountId,
                      store_id: this.storeId,
                      page: this.pagination.page,
                      page_size: this.pagination.pageSize
                  }
              );

              if (data.success) {
                  this.templates = data.data.template_list || [];
                  this.pagination.total = data.pagination?.total || 0;
                  this.$message.success(`获取 ${this.templates.length} 个模板`);
              } else {
                  this.$message.error(data.error || '获取模板失败');
              }
          } catch (error) {
              this.$message.error('获取模板失败');
              console.error('获取模板错误:', error);
          } finally {
              this.loading = false;
          }
      },

      // 同步模板
      async syncTemplates() {
          this.syncing = true;
          try {
              const { data } = await axios.post(
                  `${config.BASE_URL}/shop/api/wechat_account.php`,
                  {
                      action: 'get_templates',
                      account_id: this.accountId,
                      store_id: this.storeId
                  }
              );

              if (data.success) {
                  this.templates = data.data.template_list || [];
                  this.pagination.total = data.pagination?.total || 0;
                  this.$message.success(`同步成功，共 ${this.templates.length} 个模板`);
                  // 重置到第一页
                  this.pagination.page = 1;
                  this.fetchTemplates();
              } else {
                  this.$message.error(data.error || '同步模板失败');
              }
          } catch (error) {
              this.$message.error('同步模板失败');
              console.error('同步模板错误:', error);
          } finally {
              this.syncing = false;
          }
      },

      // 处理搜索
      handleSearch() {
          this.pagination.page = 1;
          this.fetchTemplates();
      },

      // 处理分页变化
      handlePageChange() {
          this.fetchTemplates();
      },

      // 格式化模板内容
      formatTemplateContent(content) {
          return content.replace(/{{(.*?)\.DATA}}/g, '{$1}').replace(/\n/g, ' ');
      },

      // 提取关键词
      extractKeywords(content) {
          const matches = content.match(/{{(.*?)\.DATA}}/g) || [];
          return [...new Set(matches.map(match => match.replace(/{{(.*?)\.DATA}}/, '$1')))];
      },

      // 选择模板
      selectTemplate(template) {
          this.$emit('template-selected', template);
          this.$message.success(`已选择模板: ${template.title}`);
      },

      // 复制模板ID
      copyTemplateId(templateId) {
          navigator.clipboard.writeText(templateId).then(() => {
              this.$message.success('模板ID已复制到剪贴板');
          }).catch(() => {
              // 降级方案
              const textArea = document.createElement('textarea');
              textArea.value = templateId;
              document.body.appendChild(textArea);
              textArea.select();
              document.execCommand('copy');
              document.body.removeChild(textArea);
              this.$message.success('模板ID已复制到剪贴板');
          });
      }
  }
}
</script>

<style scoped>
.template-container {
  padding: 20px;
}

.action-bar {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.template-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.template-card {
  height: fit-content;

  :deep(.el-card__header) {
      padding: 12px 20px;
      border-bottom: 1px solid #ebeef5;
  }

  :deep(.el-card__body) {
      padding: 16px 20px;
  }
}

.template-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.template-title {
  margin: 0;
  font-size: 16px;
  color: #303133;
  flex: 1;
  margin-right: 10px;
}

.template-content {
  font-size: 14px;
  color: #606266;
}

.template-industry {
  margin-bottom: 8px;
  color: #909399;
}

.template-content-text,
.template-example {
  margin-bottom: 12px;
}

.content-preview,
.example-preview {
  margin-top: 4px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
}

.template-keywords {
  margin-top: 12px;
}

.keywords-list {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.keyword-tag {
  background: #ecf5ff;
  color: #409eff;
  border: 1px solid #d9ecff;
}

.template-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.loading-container {
  padding: 40px;
}
</style>